import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  Volume2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import PracticeVoiceInput from '@/components/practice/PracticeVoiceInput';
import TextInput from '@/components/interview/TextInput';
import InputModeToggle from '@/components/interview/InputModeToggle';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import type { Question } from '@/types/database';
import type { FriendlyError } from '@/lib/errors/types';

export type AnswerState =
  | { kind: 'idle' }
  | { kind: 'transcribing' }
  | { kind: 'grading'; userAnswer: string }
  | {
      kind: 'graded';
      userAnswer: string;
      isCorrect: boolean;
      correctAnswer?: string;
    }
  | {
      kind: 'grade_error';
      userAnswer: string;
      error: FriendlyError;
      correctAnswer?: string;
    }
  | { kind: 'revealed' };

interface PracticeQuestionCardProps {
  question: Question;
  inputMode: 'voice' | 'text';
  onInputModeChange: (mode: 'voice' | 'text') => void;
  answerState: AnswerState;
  onAnswer: (text: string) => void;
  onError: (err: FriendlyError) => void;
  onReveal: () => void;
  onClose: () => void;
  onNext: () => void;
  hasNext: boolean;
}

export default function PracticeQuestionCard({
  question,
  inputMode,
  onInputModeChange,
  answerState,
  onAnswer,
  onError,
  onReveal,
  onClose,
  onNext,
  hasNext,
}: PracticeQuestionCardProps) {
  const audio = useAudioPlayer();
  const [localError, setLocalError] = useState<FriendlyError | null>(null);
  const [showReveal, setShowReveal] = useState(false);

  const handleError = (err: FriendlyError) => {
    setLocalError(err);
    onError(err);
  };

  const isBusy =
    answerState.kind === 'transcribing' || answerState.kind === 'grading';
  const isGraded = answerState.kind === 'graded';
  const isGradeError = answerState.kind === 'grade_error';
  const isCompleted = isGraded || isGradeError;
  const isRevealed = showReveal;

  useEffect(() => {
    if (
      answerState.kind === 'transcribing' ||
      answerState.kind === 'grading' ||
      answerState.kind === 'graded' ||
      answerState.kind === 'grade_error'
    ) {
      setLocalError(null);
    }
  }, [answerState.kind]);
  const acceptedAnswers =
    question.answer_config?.accepted_answers?.join(' · ') ?? '';

  const handleToggleReveal = () => {
    const willShow = !showReveal;
    setShowReveal(willShow);
    if (willShow) onReveal();
  };

  return (
    <>
      {/* ─── ZONE A · Question ─────────────────────────────────── */}
      <div className="bg-cream-warm/40 px-5 pt-6 pb-5 md:px-7 md:pt-7 md:pb-6">
        <div className="mb-3">
          <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
            {question.category}
          </span>
        </div>

        <h2 className="font-display font-light text-[24px] md:text-[28px] lg:text-[32px] text-text-primary leading-[1.18] border-l-2 border-primary/40 pl-5">
          {question.question_text}
        </h2>

        <button
          type="button"
          onClick={() => audio.playQuestion(question.id)}
          disabled={audio.isPlaying || audio.isLoading}
          className="mt-4 ml-[22px] inline-flex items-center gap-1.5 font-sans text-xs font-medium text-primary hover:text-primary-dark hover:underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
        >
          {audio.isLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Volume2 size={14} />
          )}
          {audio.isPlaying ? 'Playing…' : audio.isLoading ? 'Loading…' : 'Listen'}
        </button>
      </div>

      {/* ─── ZONE B · Practice ─────────────────────────────────── */}
      <div className="bg-card px-5 py-6 md:px-7 md:py-7 flex flex-col gap-5 border-t border-border/40">
        <div className="flex justify-center">
          <InputModeToggle
            mode={inputMode}
            onModeChange={onInputModeChange}
            disabled={isBusy}
          />
        </div>

        <div className="max-w-md mx-auto w-full">
          {inputMode === 'voice' ? (
            <PracticeVoiceInput
              onTranscript={(text) => onAnswer(text)}
              onError={handleError}
              questionKeywords={question.answer_config?.keywords ?? []}
              disabled={isBusy}
            />
          ) : (
            <TextInput onSubmit={onAnswer} disabled={isBusy} />
          )}
        </div>

        {answerState.kind === 'graded' ? (
          <div
            className={`rounded-xl p-4 border max-w-md mx-auto w-full ${
              answerState.isCorrect
                ? 'border-success/30 bg-success/10'
                : 'border-error/30 bg-error/5'
            }`}
          >
            <p
              className={`font-sans font-semibold text-sm text-center ${
                answerState.isCorrect ? 'text-success' : 'text-error'
              }`}
            >
              {answerState.isCorrect ? 'Correct' : 'Not quite'}
            </p>

            <dl className="space-y-3 text-sm mt-3 text-left">
              <div>
                <dt className="font-sans text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  Your answer
                </dt>
                <dd className="font-sans text-text-primary">
                  {answerState.userAnswer}
                </dd>
              </div>
              {!answerState.isCorrect && (
                <div>
                  <dt className="font-sans text-[10px] uppercase tracking-widest text-text-muted mb-1">
                    Correct answer
                  </dt>
                  <dd className="font-sans text-text-primary">
                    {answerState.correctAnswer ?? acceptedAnswers}
                  </dd>
                </div>
              )}
              {question.explanation && (
                <div>
                  <dt className="font-sans text-[10px] uppercase tracking-widest text-text-muted mb-1">
                    Explanation
                  </dt>
                  <dd className="font-sans text-text-secondary leading-relaxed">
                    {question.explanation}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        ) : answerState.kind === 'grade_error' ? (
          <div className="rounded-xl p-4 border border-warning/40 bg-warning-light max-w-md mx-auto w-full">
            <div className="flex items-start gap-2.5 mb-3">
              <AlertTriangle
                size={18}
                className="text-warning flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-text-primary">
                  {answerState.error.title}
                </p>
                <p className="font-sans text-sm text-text-secondary mt-1 leading-relaxed">
                  {answerState.error.message}{' '}
                  {answerState.error.action}
                </p>
              </div>
            </div>

            <dl className="space-y-3 text-sm pt-3 border-t border-border/50">
              <div>
                <dt className="font-sans text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  Your answer
                </dt>
                <dd className="font-sans text-text-primary">
                  {answerState.userAnswer}
                </dd>
              </div>
              {answerState.correctAnswer && (
                <div>
                  <dt className="font-sans text-[10px] uppercase tracking-widest text-text-muted mb-1">
                    Reference answer
                  </dt>
                  <dd className="font-sans text-text-primary">
                    {answerState.correctAnswer}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        ) : localError ? (
          <div className="rounded-xl border border-error/30 bg-error/5 p-3 text-center max-w-md mx-auto">
            <p className="font-sans font-semibold text-sm text-error">
              {localError.title}
            </p>
            <p className="font-sans text-sm text-text-secondary mt-1">
              {localError.message}
            </p>
          </div>
        ) : null}
      </div>

      {/* ─── ZONE C · Reference / Actions ─────────────────────── */}
      {!isCompleted ? (
        <div className="bg-cream-warm/40 border-t border-border/40 px-5 py-5 md:px-7 md:py-6">
          <div className="flex items-center justify-between gap-4 mb-2.5">
            <span className="font-display italic font-light text-sm text-text-secondary tracking-wide">
              Answer
            </span>
            <button
              type="button"
              onClick={handleToggleReveal}
              className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-primary hover:text-primary-dark cursor-pointer transition-colors"
              aria-label={isRevealed ? 'Hide answer' : 'Reveal answer'}
            >
              {isRevealed ? (
                <>
                  <EyeOff size={13} /> Hide
                </>
              ) : (
                <>
                  <Eye size={13} /> Reveal
                </>
              )}
            </button>
          </div>

          <div
            onClick={!isRevealed ? handleToggleReveal : undefined}
            className={`rounded-md -mx-1 px-1 py-1 transition-colors ${
              !isRevealed ? 'cursor-pointer hover:bg-cream-warm/60' : ''
            }`}
          >
            <motion.p
              animate={{
                filter: isRevealed ? 'blur(0px)' : 'blur(6px)',
                opacity: isRevealed ? 1 : 0.9,
              }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`font-display text-base md:text-lg text-text-primary leading-snug ${
                isRevealed ? '' : 'select-none'
              }`}
              aria-hidden={!isRevealed}
            >
              {acceptedAnswers}
            </motion.p>
            {question.explanation && (
              <motion.div
                aria-hidden={!isRevealed}
                initial={false}
                animate={{
                  height: isRevealed ? 'auto' : 0,
                  opacity: isRevealed ? 1 : 0,
                  marginTop: isRevealed ? 12 : 0,
                }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <p className="font-sans text-sm text-text-secondary leading-relaxed">
                  {question.explanation}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      ) : answerState.kind === 'graded' ? (
        <div className="bg-cream-warm/40 border-t border-border/40 px-5 py-5 md:px-7 md:py-6">
          <div className="flex justify-center gap-2">
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
            <Button variant="primary" onClick={onNext} disabled={!hasNext}>
              Next question
            </Button>
          </div>
        </div>
      ) : answerState.kind === 'grade_error' ? (
        <div className="bg-cream-warm/40 border-t border-border/40 px-5 py-5 md:px-7 md:py-6">
          <div className="flex justify-center gap-2">
            {answerState.error.retryable && (
              <Button
                variant="primary"
                onClick={() => onError(answerState.error)}
              >
                Try again
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
